import {
    Button,
    Checkbox,
    ErrorAlert,
    Notice,
    PasswordInput,
    QrCode,
    Spinner,
    TextInput,
    linkStyles,
} from '@lumlab/platform-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { authClient } from '../app/auth.ts';
import { AuthScreen, authErrorCode } from '../app/AuthScreen.tsx';
import { useAccountState } from '../app/account.ts';
import { factorReplacementSetup } from '@lumlab/platform-identity/contract';

export function SecurityPage() {
    const { t } = useTranslation();
    const session = authClient.useSession();
    const account = useAccountState(session.data?.user.id);
    const queryClient = useQueryClient();
    const [password, setPassword] = useState('');
    const [code, setCode] = useState('');
    const [saved, setSaved] = useState(false);
    const [setup, setSetup] = useState<{ totpURI: string; backupCodes: string[]; replacementId?: string } | null>(null);
    const [replacing, setReplacing] = useState(false);
    const [disabling, setDisabling] = useState(false);
    const [backup, setBackup] = useState(false);
    const [useRecentSession, setUseRecentSession] = useState(false);
    const [pending, setPending] = useState(false);
    const [error, setError] = useState<unknown>(null);
    const [success, setSuccess] = useState(false);
    const secret = setup ? new URL(setup.totpURI).searchParams.get('secret') : null;
    const run = async (action: () => Promise<{ error: { status: number; code?: string | undefined } | null }>) => {
        setPending(true);
        setError(null);
        setSuccess(false);
        try {
            const result = await action();
            if (result.error) setError({ code: authErrorCode(result.error) });
            else {
                await session.refetch();
                await queryClient.invalidateQueries({ queryKey: ['account-state'] });
                setPassword('');
                setCode('');
            }
        } catch {
            setError({ code: 'internal' });
        } finally {
            setPending(false);
        }
    };
    const beginChange = (disable: boolean) => {
        setReplacing(true);
        setDisabling(disable);
        setCode('');
        setPassword('');
        setBackup(false);
        setUseRecentSession(
            !!session.data && Date.now() - new Date(session.data.session.createdAt).getTime() < 5 * 60_000,
        );
        setError(null);
        setSuccess(false);
    };
    return (
        <AuthScreen title={t('platform.security.title')}>
            {account.isPending && <Spinner label={t('platform.shell.loading')} />}
            {account.error && <ErrorAlert error={account.error} />}
            {error !== null && <ErrorAlert error={error} />}
            {success && <Notice tone="success">{t('platform.security.updated')}</Notice>}
            {account.data && (
                <>
                    {account.data.twoFactorRequired && (
                        <Notice tone="warning">{t('platform.security.adminRequired')}</Notice>
                    )}
                    <p className="text-sm text-muted">
                        {account.data.twoFactorEnabled ? t('platform.security.enabled') : t('platform.security.intro')}
                    </p>
                    {setup ? (
                        <form
                            className="flex flex-col gap-4"
                            onSubmit={(event) => {
                                event.preventDefault();
                                void run(async () => {
                                    const result = setup.replacementId
                                        ? await authClient.$fetch('/two-factor/replacement/confirm', {
                                              method: 'POST',
                                              body: {
                                                  replacementId: setup.replacementId,
                                                  code: code.trim(),
                                                  codesSaved: saved,
                                              },
                                          })
                                        : await authClient.twoFactor.verifyTotp({ code: code.trim() });
                                    if (!result.error) {
                                        setSetup(null);
                                        setReplacing(false);
                                        setSuccess(true);
                                    }
                                    return result;
                                });
                            }}
                        >
                            {setup.replacementId && (
                                <Notice tone="info">{t('platform.security.replacementPending')}</Notice>
                            )}
                            <p className="text-sm">{t('platform.security.setupIntro')}</p>
                            <QrCode value={setup.totpURI} label={t('platform.security.qrCode')} />
                            <p className="text-sm text-muted">{t('platform.security.manualSetup')}</p>
                            <code
                                className="break-all rounded-lg bg-surface-muted p-3 text-sm"
                                aria-label={t('platform.security.secret')}
                            >
                                {secret}
                            </code>
                            <a className={linkStyles} href={setup.totpURI}>
                                {t('platform.security.openAuthenticator')}
                            </a>
                            <Notice tone="warning">{t('platform.security.saveCodes')}</Notice>
                            <ul className="grid grid-cols-2 gap-2 rounded-lg bg-surface-muted p-3 font-mono text-sm">
                                {setup.backupCodes.map((backup) => (
                                    <li key={backup}>{backup}</li>
                                ))}
                            </ul>
                            <Checkbox
                                label={t('platform.security.codesSaved')}
                                checked={saved}
                                onChange={(event) => setSaved(event.target.checked)}
                                required
                            />
                            <TextInput
                                label={t('platform.security.code')}
                                value={code}
                                onChange={(event) => setCode(event.target.value)}
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                required
                                pattern="[0-9]{6}"
                            />
                            <Button type="submit" disabled={!saved} loading={pending}>
                                {setup.replacementId
                                    ? t('platform.security.confirmReplacement')
                                    : t('platform.security.activate')}
                            </Button>
                            {setup.replacementId && (
                                <Button
                                    variant="default"
                                    disabled={pending}
                                    onClick={() => {
                                        void run(async () => {
                                            const result = await authClient.$fetch('/two-factor/replacement/cancel', {
                                                method: 'POST',
                                                body: { replacementId: setup.replacementId },
                                            });
                                            if (!result.error) {
                                                setSetup(null);
                                                setReplacing(false);
                                            }
                                            return result;
                                        });
                                    }}
                                >
                                    {t('platform.security.cancelReplacement')}
                                </Button>
                            )}
                        </form>
                    ) : replacing ? (
                        <form
                            className="flex flex-col gap-4"
                            onSubmit={(event) => {
                                event.preventDefault();
                                void run(async () => {
                                    const result = await authClient.$fetch(
                                        disabling ? '/two-factor/replacement/disable' : '/two-factor/replacement/start',
                                        {
                                            method: 'POST',
                                            body: {
                                                password,
                                                method: useRecentSession ? 'session' : backup ? 'backup' : 'totp',
                                                code: code.trim(),
                                            },
                                        },
                                    );
                                    if (!result.error) {
                                        if (disabling) {
                                            setReplacing(false);
                                            setSuccess(true);
                                        } else {
                                            // Better Auth revives date fields; this screen only consumes the enrollment material.
                                            setSetup(
                                                factorReplacementSetup.omit({ expiresAt: true }).parse(result.data),
                                            );
                                            setSaved(false);
                                        }
                                    }
                                    return result;
                                });
                            }}
                        >
                            <Notice tone="info">
                                {disabling
                                    ? t('platform.security.disableIntro')
                                    : t('platform.security.replacementIntro')}
                            </Notice>
                            {useRecentSession && <Notice tone="info">{t('platform.security.recentFactor')}</Notice>}
                            <PasswordInput
                                label={t('platform.auth.password')}
                                autoComplete="current-password"
                                value={password}
                                onChange={(event) => setPassword(event.target.value)}
                                required
                            />
                            {!useRecentSession && (
                                <TextInput
                                    label={
                                        backup ? t('platform.security.backupCode') : t('platform.security.currentCode')
                                    }
                                    autoComplete="one-time-code"
                                    inputMode={backup ? 'text' : 'numeric'}
                                    value={code}
                                    onChange={(event) => setCode(event.target.value)}
                                    required
                                />
                            )}
                            {!useRecentSession && (
                                <Button
                                    variant="ghost"
                                    disabled={pending}
                                    onClick={() => {
                                        setBackup(!backup);
                                        setCode('');
                                        setError(null);
                                    }}
                                >
                                    {backup
                                        ? t('platform.security.useAuthenticator')
                                        : t('platform.security.useBackup')}
                                </Button>
                            )}
                            {useRecentSession && (
                                <Button
                                    variant="ghost"
                                    disabled={pending}
                                    onClick={() => {
                                        setUseRecentSession(false);
                                        setError(null);
                                    }}
                                >
                                    {t('platform.security.useNewFactor')}
                                </Button>
                            )}
                            <Button type="submit" loading={pending}>
                                {disabling ? t('platform.security.disable') : t('platform.security.prepareReplacement')}
                            </Button>
                            <Button
                                variant="default"
                                disabled={pending}
                                onClick={() => {
                                    setReplacing(false);
                                    setPassword('');
                                    setCode('');
                                    setError(null);
                                }}
                            >
                                {t('platform.security.cancelChange')}
                            </Button>
                        </form>
                    ) : (
                        <>
                            {account.data.twoFactorEnabled && account.data.twoFactorVerified && (
                                <Button variant="default" onClick={() => beginChange(false)}>
                                    {t('platform.security.replaceAuthenticator')}
                                </Button>
                            )}
                            {account.data.twoFactorEnabled &&
                                account.data.twoFactorVerified &&
                                !account.data.twoFactorRequired && (
                                    <Button variant="default" onClick={() => beginChange(true)}>
                                        {t('platform.security.disable')}
                                    </Button>
                                )}
                            {account.data.twoFactorEnabled && !account.data.twoFactorVerified ? (
                                <Link className={linkStyles} to="/two-factor">
                                    {t('platform.security.challengeTitle')}
                                </Link>
                            ) : (
                                !account.data.twoFactorEnabled && (
                                    <form
                                        className="flex flex-col gap-4"
                                        onSubmit={(event) => {
                                            event.preventDefault();
                                            void run(async () => {
                                                const result = await authClient.twoFactor.enable({ password });
                                                if (result.data?.method === 'totp') {
                                                    setSetup({
                                                        totpURI: result.data.totpURI,
                                                        backupCodes: result.data.backupCodes,
                                                    });
                                                    setSaved(false);
                                                }
                                                return result;
                                            });
                                        }}
                                    >
                                        <PasswordInput
                                            label={t('platform.auth.password')}
                                            autoComplete="current-password"
                                            value={password}
                                            onChange={(event) => setPassword(event.target.value)}
                                            required
                                        />
                                        <Button type="submit" loading={pending}>
                                            {t('platform.security.enable')}
                                        </Button>
                                    </form>
                                )
                            )}
                            {(!account.data.twoFactorRequired ||
                                (account.data.twoFactorEnabled && account.data.twoFactorVerified)) && (
                                <Link to="/" className={linkStyles}>
                                    {t('platform.security.continue')}
                                </Link>
                            )}
                        </>
                    )}
                </>
            )}
            <Button
                variant="ghost"
                onClick={() => {
                    void authClient.signOut().then(() => {
                        queryClient.clear();
                        window.location.assign('/sign-in');
                    });
                }}
            >
                {t('platform.auth.signOut')}
            </Button>
        </AuthScreen>
    );
}
